// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import { UpdateOperation, xSeoUpdate } from "@vanmeter/App/Handlers/XNG/Seo";
import { BaseWidgetSeoModel, xSeoStateType } from "@vanmeter/App/Store/Reducers";
import { DependencyList, useEffect, useRef } from "react";

interface UseSeoSchemaOptions<T> {
    key: keyof xSeoStateType;
    data: BaseWidgetSeoModel<T>;
    enabled?: boolean;
    operation?: UpdateOperation;
}

const deleteSchema = (
    dispatch: ReturnType<typeof useAppDispatch>,
    key: keyof xSeoStateType,
    data: BaseWidgetSeoModel<unknown>,
) => {
    dispatch(xSeoUpdate({ key, operation: "delete", value: data }));
};

const useSeoSchema = <T>(
    { key, data, enabled = true }: UseSeoSchemaOptions<T>,
    dependencies: DependencyList,
): BaseWidgetSeoModel<T> | undefined => {
    const dispatch = useAppDispatch();
    const isMounted = useRef(false);

    useEffect(() => {
        if (!data) return;

        if (!enabled) {
            if (isMounted.current) {
                deleteSchema(dispatch, key, data);
            }
            return;
        }

        dispatch(xSeoUpdate({ key, value: data }));

        return () => {
            deleteSchema(dispatch, key, data);
        };
    }, [enabled, ...dependencies]);

    return data;
};

export default useSeoSchema;
